import React, { useState } from "react";
import { Button, Modal } from "@payping/web-components";
import { useAppTranslation } from "@/utils/useAppTranslation";
import { MultipleIBANForm } from "./components/MultipleIBANForm";
import { SingleIBANForm } from "./components/SingleIBANForm";
import { useIBANFormValidation } from "./hooks/useIBANFormValidation";

interface IBANEntry {
  title: string;
  iban: string;
}

interface AddNewIBANModalProps {
  open: boolean;
  onCancel: () => void;
  onFinish: (values: { title: string; iban: string } | { ibans: IBANEntry[] }) => void;
  form: any;
  isEditing?: boolean;
  loading?: boolean;
  enableMultipleIBANs?: boolean;
}

const AddNewIBANModal: React.FC<AddNewIBANModalProps> = ({
  open,
  onCancel,
  onFinish,
  form,
  isEditing = false,
  loading = false,
  enableMultipleIBANs = true,
}) => {
  const { t } = useAppTranslation();
  const [formValues, setFormValues] = useState<any>({});
  const isFormValid = useIBANFormValidation(formValues, enableMultipleIBANs ?? false);

  const isLoading = loading;

  const handleFormValuesChange = (_changedValues: any, allValues: any) => {
    setFormValues(allValues);
  };

  const handleFinish = async (values: any) => {
    if (enableMultipleIBANs) {
      // Check if values already has ibans array (from MultipleIBANForm)
      if ("ibans" in values && Array.isArray(values.ibans)) {
        // Already processed by MultipleIBANForm, just pass it through
        if (values.ibans.length === 0) {
          return;
        }
        onFinish({ ibans: values.ibans });
      } else {
        // Process raw form values (shouldn't happen in normal flow, but handle it)
        const ibans: IBANEntry[] = [
          { title: values.title1 || "", iban: values.iban1 || "" },
          { title: values.title2 || "", iban: values.iban2 || "" },
          { title: values.title3 || "", iban: values.iban3 || "" },
        ].filter((entry) => entry.iban && entry.iban.trim() !== "");

        // Prevent submitting with empty IBANs array
        if (ibans.length === 0) {
          return;
        }

        onFinish({ ibans });
      }
    } else {
      // Validate single IBAN is not empty
      if (!values.iban || values.iban.trim() === "") {
        return;
      }
      onFinish(values);
    }
  };

  if (enableMultipleIBANs) {
    return (
      <Modal
        title={t("profile.bank.addNewIBAN")}
        open={open}
        onCancel={onCancel}
        width={600}
        footer={
          <div className="tw-flex tw-w-full tw-items-start tw-justify-start tw-gap-2 tw-self-stretch md:tw-w-fit">
            <Button
              onClick={onCancel}
              className="tw-bg-white tw-border-gray-300 w-full md:w-fit tw-flex tw-h-8 tw-items-center tw-justify-center tw-gap-2 tw-rounded-md tw-border tw-pb-1 tw-pl-4 tw-pr-4 tw-pt-1"
            >
              <div className="tw-text-gray-900 tw-text-center tw-text-sm tw-font-normal tw-leading-5">
                {t("profile.bank.cancel")}
              </div>
            </Button>
            <Button
              type="primary"
              onClick={() => form.submit()}
              loading={isLoading}
              disabled={!isFormValid || isLoading}
              className="tw-bg-blue-600 w-full md:w-fit tw-flex tw-h-8 tw-items-center tw-justify-center tw-gap-2 tw-rounded-md"
            >
              <div className="tw-text-white tw-text-center tw-text-sm tw-font-normal tw-leading-5">
                {t("profile.bank.saveAndConfirm")}
              </div>
            </Button>
          </div>
        }
        closable={true}
      >
        <MultipleIBANForm form={form} onFinish={handleFinish} onFormValuesChange={handleFormValuesChange} />
      </Modal>
    );
  }

  return (
    <Modal
      title={isEditing ? t("profile.bank.edit") : t("profile.bank.addNewIBAN")}
      open={open}
      onCancel={onCancel}
      footer={null}
      width={600}
      centered
      className="add-iban-modal"
    >
      <SingleIBANForm
        form={form}
        onFinish={handleFinish}
        onFormValuesChange={handleFormValuesChange}
        isFormValid={isFormValid}
        isLoading={isLoading}
        isEditing={isEditing}
        onCancel={onCancel}
      />
    </Modal>
  );
};

export default AddNewIBANModal;
